import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, FormControl, FormLabel,
  Box, Text, Textarea, useToast, Flex, InputGroup, InputLeftElement, Icon, Badge, useDisclosure, Checkbox, Divider,
} from '@chakra-ui/react';
import { FiSearch, FiEdit2, FiArrowLeft, FiCreditCard, FiPlus } from 'react-icons/fi';
import api from '../api';
import ImageUpload from './ImageUpload';
import BankAccountsModal from './BankAccountsModal';
import SocialLinksEditor from './SocialLinksEditor';
import ServiceGroupPicker from './ServiceGroupPicker';
import ContactsListModal from './ContactsListModal';

const COMPANY_TYPES = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'group_brand', label: 'Group Brand or Company' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'joint_venture', label: 'Joint Venture' },
];

export default function CompaniesListModal({ isOpen, onClose, initialCompanyId, initialEdit }) {
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [engStatuses, setEngStatuses] = useState([]);
  const toast = useToast();
  const bankDisclosure = useDisclosure();
  const [companyServiceGroups, setCompanyServiceGroups] = useState([]);
  const [addingContact, setAddingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'' });
  const [savingContact, setSavingContact] = useState(false);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalId, setContactModalId] = useState(null);

  const fetchLookups = useCallback(async () => {
    try {
      const [ls, es] = await Promise.all([
        api.get('/lookups/lead-sources'), api.get('/lookups/engagement-statuses')]);
      setLeadSources(ls.data); setEngStatuses(es.data);
    } catch(e) {}
  }, []);

  const fetch = useCallback(async () => {
    try {
      const { data } = query.length > 0
        ? await api.get(`/companies/search?q=${encodeURIComponent(query)}`)
        : await api.get('/companies');
      setCompanies(data);
    } catch (e) {}
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    // Common housekeeping for any open
    setQuery('');
    fetch();
    fetchLookups();
    // If we were handed a specific company to jump to, skip the reset
    // and let the initialCompanyId effect below take over.
    if (initialCompanyId) return;
    setSelected(null);
    setEditing(false);
  }, [isOpen, initialCompanyId]);

  // Jump-to-edit path: opened with initialCompanyId + initialEdit set
  // (the pencil icon on the company quick-view modal). Fetch the company
  // and drop straight into the edit form.
  useEffect(() => {
    if (!isOpen || !initialCompanyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${initialCompanyId}`);
        if (cancelled) return;
        setSelected(data);
        if (initialEdit) {
          setForm({ ...data });
          setEditing(true);
        } else {
          setEditing(false);
        }
        // Load secondary data the view tab uses, same as openView.
        try { const sgRes = await api.get(`/service-groups/for/company/${initialCompanyId}`); if (!cancelled) setCompanyServiceGroups(sgRes.data); } catch(e) {}
        try { const cr = await api.get(`/contacts?company_id=${initialCompanyId}`); if (!cancelled) setCompanyContacts(cr.data || []); } catch(e) {}
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [isOpen, initialCompanyId, initialEdit]);

  useEffect(() => {
    const t = setTimeout(fetch, 200);
    return () => clearTimeout(t);
  }, [query, fetch]);

  const openEdit = (company) => {
    setSelected(company);
    setForm({ ...company });
    setEditing(true);
  };

  const openView = async (company) => {
    try {
      const { data } = await api.get(`/companies/${company.id}`);
      setSelected(data);
      setEditing(false);
      setCompanyContacts([]);
      try { const sgRes = await api.get(`/service-groups/for/company/${company.id}`); setCompanyServiceGroups(sgRes.data); } catch(e) { setCompanyServiceGroups([]); }
      // Fetch contacts for this company
      setContactsLoading(true);
      try { const cr = await api.get(`/contacts?company_id=${company.id}`); setCompanyContacts(cr.data || []); } catch(e) {}
      setContactsLoading(false);
    } catch (e) {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${selected.id}`, form);
      // Save service groups
      try { await api.put(`/service-groups/for/company/${selected.id}`, { service_group_ids: companyServiceGroups.map(s => s.id) }); } catch(e) {}
      toast({ title: 'Company updated', status: 'success', duration: 1500 });
      setEditing(false);
      await openView({ id: selected.id });
      fetch();
    } catch (e) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleChange = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const saveContact = async () => {
    if (!contactForm.first_name.trim()) { toast({ title: 'First name required', status: 'warning', duration: 1500 }); return; }
    setSavingContact(true);
    try {
      await api.post('/contacts', { ...contactForm, company_id: selected.id });
      toast({ title: 'Contact added', status: 'success', duration: 1500 });
      setAddingContact(false);
      setContactForm({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'' });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSavingContact(false);
  };

  const goBack = () => { setSelected(null); setEditing(false); };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}>
            {selected && <Icon as={FiArrowLeft} cursor="pointer" onClick={goBack} />}
            Companies
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!selected && (
            <>
              <InputGroup mb={3}>
                <InputLeftElement h="32px"><Icon as={FiSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                <Input size="sm" pl={8} placeholder="Search companies..." value={query}
                  onChange={(e) => setQuery(e.target.value)} />
              </InputGroup>
              <VStack spacing={1} align="stretch">
                {companies.map(co => (
                  <Box key={co.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                    cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => openView(co)}>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text fontSize="12px" fontWeight="600">{co.name}</Text>
                        {co.website && <Text fontSize="10px" color="gray.500">{co.website}</Text>}
                      </Box>
                      <Icon as={FiEdit2} color="gray.400" boxSize={3}
                        onClick={(e) => { e.stopPropagation(); openEdit(co); }} />
                    </Flex>
                  </Box>
                ))}
                {companies.length === 0 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No companies found</Text>
                )}
              </VStack>
            </>
          )}

          {selected && !editing && (
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="600">{selected.name}</Text>
                <Button size="xs" leftIcon={<FiEdit2 />} onClick={() => { setForm({ ...selected }); setEditing(true); }}>
                  Edit
                </Button>
              </Flex>
              {selected.engagement_status_name && (
                <Badge colorScheme="blue" fontSize="10px" w="fit-content"
                  bg={selected.engagement_color || 'gray.500'} color="white">
                  {selected.engagement_status_name}
                </Badge>
              )}
              <HStack spacing={2}>
                {selected.company_type && (
                  <Badge colorScheme="purple" fontSize="10px">{COMPANY_TYPES.find(t => t.value === selected.company_type)?.label || selected.company_type}</Badge>
                )}
                {companyServiceGroups.length > 0 && companyServiceGroups.map(sg => (
                  <Badge key={sg.id} colorScheme="orange" fontSize="10px">{sg.name}</Badge>
                ))}
              </HStack>
              {[
                ['Company ID', selected.company_code],
                ['Website', selected.website],
                ['Phone', selected.phone],
                ['Address', selected.address],
                ['Company No.', selected.company_number],
                ['VAT No.', selected.vat_number],
                ['Lead Source', selected.lead_source_name || selected.lead_source],
                ['Referral', selected.referral_first_name ? `${selected.referral_first_name} ${selected.referral_last_name || ''}` : null],
              ].filter(([,v]) => v).map(([label, val]) => (
                <Box key={label}>
                  <Text fontSize="10px" color="gray.500" fontWeight="600" textTransform="uppercase">{label}</Text>
                  <Text fontSize="12px">{val}</Text>
                </Box>
              ))}
              <SocialLinksEditor entityType="company" entityId={selected.id} />
              {(selected.po_required || selected.invoicing_notes) && (
                <Box bg="cyan.50" p={2} borderRadius="md" border="1px" borderColor="cyan.200">
                  <Text fontSize="9px" fontWeight="700" color="cyan.700" textTransform="uppercase" mb={1}>Invoicing</Text>
                  {selected.po_required && <Badge colorScheme="cyan" fontSize="9px" mb={1}>PO Required</Badge>}
                  {selected.invoicing_notes && <Text fontSize="11px" color="gray.700">{selected.invoicing_notes}</Text>}
                </Box>
              )}

              {/* ── Contacts list ── */}
              <Box>
                <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                  Contacts {companyContacts.length > 0 && `(${companyContacts.length})`}
                </Text>
                {contactsLoading ? (
                  <Text fontSize="11px" color="gray.400">Loading…</Text>
                ) : companyContacts.length === 0 ? (
                  <Text fontSize="11px" color="gray.400">No contacts yet.</Text>
                ) : (
                  <VStack spacing={1} align="stretch">
                    {companyContacts.map(ct => (
                      <Flex key={ct.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                        cursor="pointer" _hover={{ bg: 'teal.50', borderColor: 'teal.200' }}
                        align="center" justify="space-between"
                        onClick={() => { setContactModalId(ct.id); setContactModalOpen(true); }}>
                        <Box>
                          <Text fontSize="12px" fontWeight="600">{ct.first_name} {ct.last_name || ''}</Text>
                          <HStack spacing={2} fontSize="10px" color="gray.500">
                            {ct.job_title && <Text>{ct.job_title}</Text>}
                            {ct.email && <Text>{ct.email}</Text>}
                            {ct.mobile && <Text>{ct.mobile}</Text>}
                          </HStack>
                        </Box>
                        <Icon as={FiEdit2} color="gray.300" boxSize={3} flexShrink={0} />
                      </Flex>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}

          {selected && editing && (
            <VStack spacing={3}>
              <Flex w="100%" justify="center">
                <ImageUpload
                  folder="logos" entityType="company" entityId={selected.id}
                  currentFileId={form.logo_file_id || null}
                  onUpdate={(fileId) => { setForm(p => ({ ...p, logo_file_id: fileId })); }}
                  size="80px" label="Logo"
                />
              </Flex>
              {[
                ['name', 'Company Name *', 'input'],
                ['company_code', 'Company ID (Sage etc.)', 'input'],
                ['website', 'Website', 'input'],
                ['phone', 'Phone', 'input'],
                ['address', 'Address', 'textarea'],
                ['company_number', 'Company Number', 'input'],
                ['vat_number', 'VAT Number', 'input'],
              ].map(([field, label, type]) => (
                <FormControl key={field}>
                  <FormLabel fontSize="xs" mb={0.5}>{label}</FormLabel>
                  {type === 'textarea' ? (
                    <Textarea size="sm" value={form[field] || ''} onChange={handleChange(field)} rows={2} />
                  ) : (
                    <Input size="sm" value={form[field] || ''} onChange={handleChange(field)} />
                  )}
                </FormControl>
              ))}
              <HStack w="100%" spacing={2}>
                <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Company Type</FormLabel>
                  <Select size="sm" value={form.company_type || 'customer'} onChange={handleChange('company_type')}>
                    {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select></FormControl>
                <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Lead Source</FormLabel>
                  <Select size="sm" value={form.lead_source_id || ''} onChange={handleChange('lead_source_id')}>
                    <option value="">—</option>
                    {leadSources.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Select></FormControl>
                <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Engagement Status</FormLabel>
                  <Select size="sm" value={form.engagement_status_id || ''} onChange={handleChange('engagement_status_id')}>
                    <option value="">—</option>
                    {engStatuses.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </Select></FormControl>
              </HStack>
              <FormControl><FormLabel fontSize="xs" mb={0.5}>Services &amp; Product Groups</FormLabel>
                <ServiceGroupPicker selected={companyServiceGroups} onChange={setCompanyServiceGroups} />
              </FormControl>
              {selected && <SocialLinksEditor entityType="company" entityId={selected.id} />}
              <Box bg="cyan.50" p={3} borderRadius="md" border="1px" borderColor="cyan.200" w="100%">
                <Text fontSize="9px" fontWeight="700" color="cyan.700" textTransform="uppercase" mb={2}>Invoicing Workflow</Text>
                <VStack spacing={2}>
                  <Checkbox size="sm" isChecked={!!form.po_required} onChange={e => setForm(p => ({ ...p, po_required: e.target.checked }))} colorScheme="cyan" w="100%">
                    <Text fontSize="12px" fontWeight="600" color="cyan.800">PO Required for this company</Text>
                  </Checkbox>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Invoicing Notes</FormLabel>
                    <Textarea size="sm" rows={2} value={form.invoicing_notes || ''}
                      onChange={e => setForm(p => ({ ...p, invoicing_notes: e.target.value }))}
                      placeholder="e.g. Always include PO number, send to accounts@company.com..." />
                  </FormControl>
                </VStack>
              </Box>
              <HStack w="100%" justify="space-between">
                <Button size="sm" leftIcon={<FiCreditCard />} variant="outline" colorScheme="cyan"
                  onClick={bankDisclosure.onOpen}>Bank Accounts</Button>
                <HStack>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                    onClick={handleSave} isLoading={saving}>Save</Button>
                </HStack>
              </HStack>
            </VStack>
          )}

          {/* ── Add Contact inline form ── */}
          {addingContact && selected && (
            <Box mt={2} p={3} border="1px" borderColor="teal.200" borderRadius="md" bg="teal.50">
              <Text fontSize="13px" fontWeight="700" color="teal.700" mb={3}>
                New Contact for {selected.name}
              </Text>
              <VStack spacing={3} align="stretch">
                <HStack spacing={2}>
                  <FormControl isRequired flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>First Name *</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.first_name} onChange={e => setContactForm(p=>({...p,first_name:e.target.value}))} autoFocus />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Last Name</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.last_name} onChange={e => setContactForm(p=>({...p,last_name:e.target.value}))} />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Email</FormLabel>
                  <Input size="sm" bg="white" type="email" value={contactForm.email} onChange={e => setContactForm(p=>({...p,email:e.target.value}))} />
                </FormControl>
                <HStack spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Mobile</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.mobile} onChange={e => setContactForm(p=>({...p,mobile:e.target.value}))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Landline</FormLabel>
                    <Input size="sm" bg="white" value={contactForm.landline} onChange={e => setContactForm(p=>({...p,landline:e.target.value}))} />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Job Title</FormLabel>
                  <Input size="sm" bg="white" value={contactForm.job_title} onChange={e => setContactForm(p=>({...p,job_title:e.target.value}))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Notes / Description</FormLabel>
                  <Textarea size="sm" bg="white" rows={2} value={contactForm.description} onChange={e => setContactForm(p=>({...p,description:e.target.value}))} placeholder="Any notes about this contact..." />
                </FormControl>
                <HStack justify="flex-end" spacing={2}>
                  <Button size="sm" variant="ghost" onClick={() => setAddingContact(false)}>Cancel</Button>
                  <Button size="sm" colorScheme="teal" isLoading={savingContact} onClick={saveContact}>Save Contact</Button>
                </HStack>
              </VStack>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          {selected && !editing && !addingContact && (
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="teal" variant="outline" mr="auto"
              onClick={() => { setContactForm({ first_name:'', last_name:'', email:'', mobile:'', landline:'', job_title:'', description:'' }); setAddingContact(true); }}>
              Add Contact
            </Button>
          )}
          {addingContact && (
            <Button size="sm" variant="ghost" mr="auto" onClick={() => setAddingContact(false)}>Cancel</Button>
          )}
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {selected && (
      <BankAccountsModal isOpen={bankDisclosure.isOpen} onClose={bankDisclosure.onClose}
        companyId={selected.id} title={`Bank Accounts — ${selected.name}`} />
    )}
    <ContactsListModal
      isOpen={contactModalOpen}
      onClose={() => { setContactModalOpen(false); setContactModalId(null); }}
      initialContactId={contactModalId}
    />
    </>
  );
}
